import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as glMatrix from 'gl-matrix';
import * as socketIoClient from 'socket.io-client';
import { ReactSVG as Svg } from 'react-svg';

import {
  deserializePreviewSuccessResponse,
  PreviewRequest,
  previewResponseSchema,
} from '@larki/shared/preview';

import shader from '../../shaders/previewTest.wgsl';
import { radians } from '../../utilities/math';
import {
  createGpuBufferFromFloat,
  createGpuBufferFromMat4,
  createGpuBufferFromVec2,
} from '../../utilities/wgpu';
import { Vec3Input } from '../../types/gl';
import classNames from 'classnames';
import text from '../../text';
import Img from '../img/Img';

function sphericalToCartesian(r: number, theta: number, phi: number) {
  const x = r * Math.sin(phi) * Math.cos(theta);
  const y = r * Math.sin(phi) * Math.sin(theta);
  const z = r * Math.cos(phi);
  return glMatrix.vec3.fromValues(x, y, z);
}

class PreviewClient {
  private socket: socketIoClient.Socket;
  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.socket = socketIoClient.io(process.env.PREVIEW_SERVER_URL!, {
      transports: ['websocket'],
      path: '/ps',
      secure: true,
    });
    this.#establishListeners();
    console.log(':: client created');
  }
  #establishListeners() {
    this.socket.on('connect', () => {
      console.log(
        `:: connected with transport ${this.socket.io.engine.transport.name}`
      );
    });
    this.socket.on('connect_error', (error) => {
      console.error(':: connect_error', error);
    });
    this.socket.on('disconnect', (reason) => {
      console.log(':: disconnect', reason);
    });
  }
  async getPreview(id: string, lod: number, offset?: Float32Array) {
    console.log(':: requesting preview');
    const _raw = await this.socket.emitWithAck('preview', {
      id,
      lod,
    } satisfies PreviewRequest);
    const raw = previewResponseSchema.parse(_raw);

    // error handling
    if (raw.$status === 'error') {
      // safely handle not found case
      if (raw.$code === 'NOT_FOUND') {
        return null;
      }
      throw new Error(raw.error);
    }

    const { xyz, rgb, ...extra } = deserializePreviewSuccessResponse(raw);
    if (!offset) {
      offset = extra.offset;
    }

    // 3 bytes for xyz data, 1 byte for rgb data
    const numPoints = xyz.length / 3;
    const data = new Float32Array(numPoints * 4);

    // clever space-efficient way of packing rgb data into same buffer
    // https://webgpufundamentals.org/webgpu/lessons/webgpu-vertex-buffers.html
    const rgbData = new Uint8Array(data.buffer);

    const scaleFactor = 1 / 65535;
    for (let i = 0; i < numPoints; i++) {
      const i4 = i * 4;
      const i3 = i * 3;
      data[i4 + 0] = xyz[i3 + 0] * scaleFactor + offset[0];
      data[i4 + 1] = xyz[i3 + 1] * scaleFactor + offset[1];
      data[i4 + 2] = xyz[i3 + 2] * scaleFactor + offset[2];

      const j = i * 16 + 12;
      rgbData[j + 0] = rgb[i3 + 0];
      rgbData[j + 1] = rgb[i3 + 1];
      rgbData[j + 2] = rgb[i3 + 2];
    }

    return { buffer: data, offset };
  }
}

// by creating the client here we know there is only one
const client = new PreviewClient();

// WGPU \/
const UP = glMatrix.vec3.fromValues(0, 0, 1);
const ZERO = glMatrix.vec3.zero(glMatrix.vec3.create());
const FOVY = radians(80);
const NEAR = 0.01;
const FAR = 5;
const TIME_TO_360 = 30;
const RADIUS = 0.2;
const PHI = radians(45);
const DEFAULT_POINT_SIZE = 2;

const generateViewMatrix = (eye: Vec3Input) => {
  const viewMatrix = glMatrix.mat4.lookAt(
    glMatrix.mat4.create(),
    eye,
    ZERO,
    UP
  );
  return viewMatrix;
};

const generateProjectionMatrix = (aspectRatio: number) => {
  const projectionMatrix = glMatrix.mat4.perspective(
    glMatrix.mat4.create(),
    FOVY,
    aspectRatio,
    NEAR,
    FAR
  );
  return projectionMatrix;
};

const prepareTimestampQuery = (device: GPUDevice) => {
  const querySet = device.createQuerySet({
    type: 'timestamp',
    count: 2,
  });
  const resolveBuffer = device.createBuffer({
    size: querySet.count * 8,
    usage: GPUBufferUsage.QUERY_RESOLVE | GPUBufferUsage.COPY_SRC,
  });
  const resultBuffer = device.createBuffer({
    size: resolveBuffer.size,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });
  return { querySet, resolveBuffer, resultBuffer };
};

type PreviewStatus = 'success' | 'unavailable' | 'loading';

export const PreviewProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // fundamental error; if this happens no previews can be shown
  const [wgpuError, setWgpuError] = useState<Error | null>(null);
  // const frameRef = useRef<number | null>(null);
  // const infoRef = useRef<HTMLPreElement>(null);
  const [id2Status, setId2Status] = useState<Map<string, PreviewStatus>>(
    new Map()
  );
  // UID: unique ID that is constant for the lifetime of the component
  // ID: non-unique ID used to query preview from the server
  //  two <Preview />s may have the same ID e.g. one small preview and aonther full-screen preview
  // const canvas2Id = useRef<Map<HTMLCanvasElement, string>>(new Map());
  const previewData = useRef<
    Map<
      string,
      | { $status: 'loading' }
      | { $status: 'success'; buffer: GPUBuffer; numPoints: number }
      | { $status: 'unavailable' }
    >
  >(new Map());
  const [wgpuDevice, setWgpuDevice] = useState<GPUDevice | null>(null);

  const [_previews, _setPreviews] = useState<Map<HTMLCanvasElement, string>>(new Map());

  const t0 = useRef<number | null>(null);

  const _init = useCallback(async (device: GPUDevice) => {
    // get all canvases with data-preview attribute
    const canvases = Array.from(
      document.querySelectorAll('canvas[data-preview]')
    ).filter((el) => el instanceof HTMLCanvasElement);

    if (!canvases.length) {
      console.warn('wgpu() :: no canvases found');
      return () => {};
    }

    // bind group layout
    const bindGroupLayout = device.createBindGroupLayout({
      entries: [
        // projection
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: 'uniform' },
        },
        // view
        {
          binding: 1,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: 'uniform' },
        },
        // point size
        {
          binding: 2,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: 'uniform' },
        },
        // resolution
        {
          binding: 3,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: 'uniform' },
        },
      ],
    });

    const format = navigator.gpu.getPreferredCanvasFormat();

    // canvas resizing
    // source: https://webgpufundamentals.org/webgpu/lessons/webgpu-resizing-the-canvas.html
    const resizeObserver = new ResizeObserver((entries) => {
      // wrapping in requestAnimationFrame fixes resize errors; not sure why... https://github.com/floating-ui/floating-ui/issues/1740#issuecomment-1156101678
      // requestAnimationFrame(() => {
      for (const entry of entries) {
        const width =
          entry.devicePixelContentBoxSize?.[0].inlineSize ||
          entry.contentBoxSize[0].inlineSize * devicePixelRatio;
        const height =
          entry.devicePixelContentBoxSize?.[0].blockSize ||
          entry.contentBoxSize[0].blockSize * devicePixelRatio;
        const canvas = entry.target;
        if (!(canvas instanceof HTMLCanvasElement))
          throw new Error('expected canvas');
        canvas.width = Math.max(
          1,
          Math.min(width, device.limits.maxTextureDimension2D)
        );
        canvas.height = Math.max(
          1,
          Math.min(height, device.limits.maxTextureDimension2D)
        );
        const info = canvas2Info.get(canvas);
        if (!info) return;
        info.depthTexture.destroy();
        // const resolution = [canvas.width, canvas.height] as const;
        // device.queue.writeBuffer(
        //   resolutionBuffer,
        //   0,
        //   new Float32Array(resolution)
        // );
        // if (depthTexture) {
        //   depthTexture.destroy();
        // }
        info.depthTexture = device.createTexture({
          size: {
            width: canvas.width,
            height: canvas.height,
          },
          format: 'depth24plus',
          usage: GPUTextureUsage.RENDER_ATTACHMENT,
        });
        info.depthTextureView = info.depthTexture.createView();

        // if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
        // frameRef.current = requestAnimationFrame(render);
      }
      // });
    });

    const visibleCanvases = new Set<HTMLCanvasElement>();
    const intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!(entry.target instanceof HTMLCanvasElement)) continue;
        if (entry.isIntersecting) {
          visibleCanvases.add(entry.target);
        } else {
          visibleCanvases.delete(entry.target);
        }
      }
    });

    const canvas2Info = new Map(
      canvases.map((canvas) => {
        const context = canvas.getContext('webgpu');
        if (!context) throw new Error('No webgpu context found');

        context.configure({
          device,
          format,
          usage: GPUTextureUsage.RENDER_ATTACHMENT,
          alphaMode: 'opaque',
        });

        // constant, UNCHANGING attributes; making this reactive is additional work
        const attrs = {
          pointSize: canvas.getAttribute('data-point-size'), // optional
        };
        const pointSize = attrs.pointSize
          ? parseFloat(attrs.pointSize)
          : DEFAULT_POINT_SIZE;

        // projection matrix
        const aspectRatio = canvas.width / canvas.height;
        const projectionMatrix = generateProjectionMatrix(aspectRatio);
        const projectionMatrixBuffer = createGpuBufferFromMat4(
          device,
          projectionMatrix,
          GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
        );

        // view matrix
        const eye = sphericalToCartesian(RADIUS, 0, PHI);
        const viewMatrix = generateViewMatrix(eye);
        const viewMatrixBuffer = createGpuBufferFromMat4(
          device,
          viewMatrix,
          GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
        );

        // point size
        const pointSizeBuffer = createGpuBufferFromFloat(
          device,
          pointSize,
          GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
        );

        // resolution
        const resolution = [canvas.width, canvas.height] as const;
        const resolutionBuffer = createGpuBufferFromVec2(
          device,
          resolution,
          GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
        );

        // bind group
        const bindGroup = device.createBindGroup({
          layout: bindGroupLayout,
          entries: [
            {
              binding: 0,
              resource: {
                buffer: projectionMatrixBuffer,
              },
            },
            {
              binding: 1,
              resource: {
                buffer: viewMatrixBuffer,
              },
            },
            {
              binding: 2,
              resource: {
                buffer: pointSizeBuffer,
              },
            },
            {
              binding: 3,
              resource: {
                buffer: resolutionBuffer,
              },
            },
          ],
        });

        const depthTexture = device.createTexture({
          size: {
            width: canvas.width,
            height: canvas.height,
          },
          format: 'depth24plus',
          usage: GPUTextureUsage.RENDER_ATTACHMENT,
        });
        const depthTextureView = depthTexture.createView();

        return [
          canvas,
          {
            canvas,
            context,
            projectionMatrixBuffer,
            viewMatrixBuffer,
            pointSizeBuffer,
            resolutionBuffer,
            bindGroup,
            depthTexture,
            depthTextureView,
          },
        ];
      })
    );

    // shader module
    const shaderModule = device.createShaderModule({ code: shader });

    // pipeline layout
    const pipelineLayout = device.createPipelineLayout({
      bindGroupLayouts: [bindGroupLayout],
    });

    // pipeline
    const pipeline = device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: {
        module: shaderModule,
        entryPoint: 'vs_main',
        buffers: [
          // instance data
          {
            arrayStride: 3 * 4 + 4,
            stepMode: 'instance',
            attributes: [
              {
                shaderLocation: 0,
                format: 'float32x3',
                offset: 0,
              },
              {
                shaderLocation: 1,
                format: 'unorm8x4',
                offset: 3 * 4,
              },
            ],
          },
        ],
      },
      fragment: {
        module: shaderModule,
        entryPoint: 'fs_main',
        targets: [
          {
            format,
          },
        ],
      },
      primitive: {
        topology: 'triangle-list',
        cullMode: 'back',
        frontFace: 'ccw',
      },
      depthStencil: {
        depthWriteEnabled: true,
        depthCompare: 'less-equal',
        format: 'depth24plus',
      },
    });

    // const { querySet, resolveBuffer, resultBuffer } = canTimestamp
    //   ? prepareTimestampQuery(device)
    //   : {};

    // const fpsAverage = new NonNegativeRollingAverage();
    // const jsAverage = new NonNegativeRollingAverage();
    // const gpuAverage = new NonNegativeRollingAverage();

    // let t0: number | null = null;
    // let then = 0;
    // let gpuTime = 0;
    // let first = true;
    let frameRef: number | null = null;
    let lastFrameTime = 0;
    const FRAME_DURATION = 1000 / 30; // 15 fps

    function render() {
      const now = performance.now();
      const deltaTime = now - lastFrameTime;

      if (deltaTime < FRAME_DURATION) {
        frameRef = requestAnimationFrame(render);
        return;
      }

      lastFrameTime = now;

      if (!canvas2Info.size) throw new Error('No infos found');

      const commandEncoder = device.createCommandEncoder();

      // const now = performance.now();
      // const renderStartTime = now;
      // const deltaTime = now - then;
      // then = now;

      if (!t0.current) t0.current = performance.now();
      const elapsed = (performance.now() - t0.current) / 1000;
      const percent = (elapsed % TIME_TO_360) / TIME_TO_360;
      const theta = percent * 2 * Math.PI;
      const eye = sphericalToCartesian(RADIUS, theta, PHI);

      const viewMatrix = generateViewMatrix(eye);

      // console.log(`render() :: visibleCanvases.size=${visibleCanvases.size} {initializationId=${initializationId}}`);

      for (const [
        canvas,
        { context, viewMatrixBuffer, depthTextureView, bindGroup },
      ] of canvas2Info.entries()) {
        // ignore canvases not in viewport
        if (!visibleCanvases.has(canvas)) continue;
        // ignore canvases that are paused
        if (canvas.getAttribute('data-pause') === 'true') continue;

        const id = _previews.get(canvas);
        if (!id) continue;

        const vertexBuffer = previewData.current.get(id);
        if (!vertexBuffer || vertexBuffer.$status !== 'success') continue;

        device.queue.writeBuffer(
          viewMatrixBuffer,
          0,
          new Float32Array(viewMatrix)
        );

        const passDescriptor: GPURenderPassDescriptor = {
          colorAttachments: [
            {
              view: context.getCurrentTexture().createView(),
              clearValue: [0.1, 0.2, 0.3, 1],
              loadOp: 'clear',
              storeOp: 'store',
            },
          ],
          depthStencilAttachment: {
            view: depthTextureView,
            depthClearValue: 1,
            depthLoadOp: 'clear',
            depthStoreOp: 'store',
          },
        };

        const passEncoder = commandEncoder.beginRenderPass(passDescriptor);

        passEncoder.setPipeline(pipeline);
        passEncoder.setBindGroup(0, bindGroup);
        passEncoder.setVertexBuffer(0, vertexBuffer.buffer);
        passEncoder.draw(6, vertexBuffer.numPoints);
        passEncoder.end();

        // submit = true;
      }

      // if (submit) {
      device.queue.submit([commandEncoder.finish()]);
      // }

      // if (canTimestamp) {
      //   if (!querySet)
      //     throw new Error('querySet undefined despite canTimestamp');
      //   passDescriptor.timestampWrites = {
      //     querySet,
      //     beginningOfPassWriteIndex: 0,
      //     endOfPassWriteIndex: 1,
      //   };
      // }

      // passEncoder.setViewport(
      //   0,
      //   0,
      //   canvasRef.current.width,
      //   canvasRef.current.height,
      //   0,
      //   1
      // );

      // if (canTimestamp) {
      //   if (!querySet || !resolveBuffer || !resultBuffer)
      //     throw new Error('unreachable');
      //   // take the results of the query and puts them into resolve buffer
      //   commandEncoder.resolveQuerySet(
      //     querySet,
      //     0,
      //     querySet.count,
      //     resolveBuffer,
      //     0
      //   );
      //   if (resultBuffer.mapState === 'unmapped') {
      //     // copy resolveBuffer to resultBuffer
      //     commandEncoder.copyBufferToBuffer(
      //       resolveBuffer,
      //       0,
      //       resultBuffer,
      //       0,
      //       resultBuffer.size
      //     );
      //   }
      // }

      //         if (canTimestamp) {
      //           if (!resultBuffer) throw new Error('unreachable');
      //           if (resultBuffer.mapState === 'unmapped') {
      //             resultBuffer.mapAsync(GPUMapMode.READ).then(() => {
      //               const times = new BigInt64Array(resultBuffer.getMappedRange());
      //               gpuTime = Number(times[1] - times[0]);
      //               gpuAverage.addSample(gpuTime / 1000);
      //               resultBuffer.unmap();
      //             });
      //           }
      //         }

      //         const jsTime = performance.now() - renderStartTime;

      //         fpsAverage.addSample(1000 / deltaTime);
      //         jsAverage.addSample(jsTime);

      //         if (infoRef.current) {
      //           infoRef.current.textContent = `\
      // fps: ${fpsAverage.get().toFixed(1)}
      // js: ${jsAverage.get().toFixed(1)}ms
      // gpu: ${canTimestamp ? `${gpuAverage.get().toFixed(1)}µs` : 'N/A'}
      // `;
      //         }

      frameRef = requestAnimationFrame(render);
    }
    frameRef = requestAnimationFrame(render);

    for (const [canvas] of canvas2Info.entries()) {
      try {
        resizeObserver.observe(canvas, {
          box: 'device-pixel-content-box',
        });
      } catch {
        resizeObserver.observe(canvas, { box: 'content-box' });
      }

      intersectionObserver.observe(canvas);
    }

    return () => {
      if (frameRef !== null) cancelAnimationFrame(frameRef);
    };
  }, [_previews]);

  /**
   * Initialize everything WGPU-related.
   * NOTE: all <Preview /> components must be rendered but not necessarily visible.
   * So if they are behind a popup, make sure opening/closing that popup is a matter of changing it's visibility, not removing/adding it to the DOM.
   */
  useEffect(() => {
    async function wgpu() {
      if (!navigator.gpu)
        throw new Error('WebGPU not supported in this browser');
      const adapter = await navigator.gpu.requestAdapter();
      if (!adapter) throw new Error('No adapter found');
      const canTimestamp = adapter.features.has('timestamp-query');
      const device = await adapter?.requestDevice({
        requiredFeatures: [
          ...(canTimestamp ? (['timestamp-query'] as const) : []),
        ],
      });

      device.addEventListener('uncapturederror', (event) => {
        throw event.error;
      });

      window.addEventListener('error', (e) => {
        console.error('window.error', e.message);
      });

      setWgpuDevice(device);
    }

    wgpu().catch((error) => {
      setWgpuError(error);
    });
  }, []);

  /**
   * Load a preview.
   * We pass in the device by argument here as assurance that the device is available.
   */
  const load = useCallback(
    async (device: GPUDevice, id: string) => {
      if (previewData.current.has(id)) return; // load ONCE only
      previewData.current.set(id, { $status: 'loading' });
      setId2Status((prev) => new Map(prev).set(id, 'loading'));

      const data = await client.getPreview(id, 0);

      // preview unavailable
      if (!data) {
        previewData.current.set(id, { $status: 'unavailable' });
        // to trigger a re-render, creating a new object is *required*, source: https://react.dev/learn/updating-objects-in-state
        setId2Status((prev) => new Map(prev).set(id, 'unavailable'));
        return;
      }

      // preview available
      const { buffer } = data;
      const numPoints = buffer.length / 4;
      console.log(`load() :: numPoints=${numPoints}`);
      const vertexBuffer = device.createBuffer({
        size: buffer.byteLength,
        usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      const xyzArray = new Float32Array(vertexBuffer.getMappedRange());
      xyzArray.set(buffer);
      vertexBuffer.unmap();
      previewData.current.set(id, {
        $status: 'success',
        buffer: vertexBuffer,
        numPoints,
      });
      setId2Status((prev) => new Map(prev).set(id, 'success'));

      // TODO: load higher LOD

      // client
      //   .getPreview(KEY, 1, offset)
      //   .then(async (data) => {
      //     if (!data) return;
      //     const { buffer } = data;
      //     await new Promise((resolve) => setTimeout(resolve, 1000));
      //     vertexBuffer.destroy();
      //     numPoints = buffer.length / 4;
      //     console.log('numPoints', numPoints);
      //     vertexBuffer = device.createBuffer({
      //       size: buffer.byteLength,
      //       usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
      //       mappedAtCreation: true,
      //     });
      //     const xyzArray = new Float32Array(vertexBuffer.getMappedRange());
      //     xyzArray.set(buffer);
      //     vertexBuffer.unmap();
      //   })
      //   .catch((err) => {
      //     throw err;
      //   });
    },
    []
  );

  const addPreview = useCallback((canvas: HTMLCanvasElement, id: string) => {
    _setPreviews((prev) => new Map([...prev, [canvas, id]]));
  }, []);

  const removePreview = useCallback((canvas: HTMLCanvasElement) => {
    _setPreviews((prev) => new Map([...prev].filter(([c]) => c !== canvas)));
  }, []);

  useEffect(() => {
    if (wgpuDevice) _init(wgpuDevice);
  }, [_init, wgpuDevice]);

  return (
    <PreviewContext.Provider
      value={{
        wgpuDevice,
        wgpuError,
        load,
        id2Status,
        addPreview,
        removePreview,
      }}
    >
      {children}
    </PreviewContext.Provider>
  );
};

export const PreviewContext = createContext<{
  wgpuDevice: GPUDevice | null;
  wgpuError: Error | null;
  id2Status: Map<string, PreviewStatus>;
  load: (
    wgpuDevice: GPUDevice,
    id: string
  ) => Promise<void>;
  addPreview: (canvas: HTMLCanvasElement, id: string) => void;
  removePreview: (canvas: HTMLCanvasElement) => void;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
}>(null!);

export const Preview = ({
  id,
  pointSize,
  pause = false,
}: {
  id?: string | null;
  pointSize?: number;
  pause?: boolean;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { id2Status, addPreview, removePreview } =
    useContext(PreviewContext);

  const status = useMemo(
    () => (id ? id2Status.get(id) : null),
    [id, id2Status]
  );

  // useEffect(() => {
  //   if (wgpuDevice && id)
  //     load(wgpuDevice, id);
  // }, [id, load, wgpuDevice]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!id || !canvas) return () => {};
    addPreview(canvas, id);
    return () => removePreview(canvas);
  }, [addPreview, id, removePreview]);

  if (status === 'unavailable') return null;

  return (
    <div className={classNames('preview', status === 'loading' && 'loading')}>
      {status === 'loading' ? (
        <div className='preview__loading'>
          <Svg src='/public/img/larki-logo-preview.svg' />
          <p>{text('loadingPreview')}</p>
        </div>
      ) : null}
      <canvas
        data-preview
        data-preview-id={id}
        data-point-size={pointSize}
        data-pause={pause}
        ref={canvasRef}
      />
    </div>
  );
};
